import capitalize from 'lodash/capitalize';
import classnames from 'classnames';
import dayjs from 'dayjs';
import { Button, TagPill } from 'components/common';
import { billing, format } from 'libs';
import { SolidStarIcon } from 'icons';

interface ResumeAddon {
  active_discount?: { coupon: { percent_off?: number } };
  eligible_discount?: { percent_off?: number };
  ends_at?: string;
  price: {
    description?: string;
    frequency?: string;
    link?: string;
    name?: string;
    price?: number;
    stripe_price?: string;
    subscription_name?: string;
  };
  type?: string;
}

interface ResumeAddonSubscriptionProps {
  hasFreePlatinum: boolean;
  isPastDue: boolean;
  onPlatinumGracePeriod: boolean;
  onResume: () => void;
  subscription: ResumeAddon;
}

const ResumeAddonSubscription = ({
  hasFreePlatinum,
  isPastDue,
  onPlatinumGracePeriod,
  onResume,
  subscription,
}: ResumeAddonSubscriptionProps) => (
  <div className="card" data-testid={`subscriptions-resume-addon-card-${subscription.type}`}>
    <div className="card-body">
      <div>
        <div className="align-items-center d-flex">
          <img alt={`${subscription.type} icon`} height={76} src={`/images/${subscription.type}.png`} width={76} />
          <div className="flex-column ms-3">
            <div className="align-items-center d-flex flex-row">
              <h4 className="fw-bold mb-0 me-2">{subscription.price.name}</h4>
              {subscription.active_discount && <TagPill icon={<SolidStarIcon />}>One month discount</TagPill>}
            </div>
            <div className="fw-bold text-1_125">
              <span
                className={classnames({ 'pe-1 text-line-through text-heading-light': subscription.active_discount })}
              >
                ${subscription.price.price! / 100} / {capitalize(subscription.price.frequency)}
              </span>
              {subscription.active_discount && (
                <span className="fw-bold">
                  {format.currency(billing.discount(subscription.price, subscription.active_discount.coupon) / 100)}
                </span>
              )}
            </div>
          </div>
        </div>
        <div className="my-3">
          {isPastDue ? (
            <span>
              You cannot resume your add-on subscription while any of your subscription payments are past due.
            </span>
          ) : (
            <>
              <span>
                Your add-on subscription is scheduled to end on{' '}
                {dayjs(subscription.ends_at).format('dddd, MMMM D, YYYY')}. If you&apos;d like to keep access to your{' '}
                {subscription.price.name} add-on, you can resume your monthly subscription before the scheduled end
                date.
              </span>
              {subscription.eligible_discount && (
                <div className="fw-bold mt-2">
                  Resume now and get {subscription.eligible_discount.percent_off}% off this add-on subscription on your
                  next bill. Discount available for one month only.
                </div>
              )}
              {subscription.active_discount && (
                <div className="fw-bold mt-2">
                  Resume before your free trial ends to keep your {subscription.active_discount.coupon.percent_off}%
                  discount.
                </div>
              )}
            </>
          )}
        </div>
      </div>
      <Button
        disabled={(!hasFreePlatinum && onPlatinumGracePeriod) || isPastDue}
        onClick={onResume}
        text="bold"
        testId={`subscriptions-resume-addon-button-${subscription.type}`}
        variant="primary-dark"
      >
        Resume add-on
      </Button>
      {!hasFreePlatinum && onPlatinumGracePeriod && (
        <div className="mt-3 text-0_75">
          You must resume your platinum subscription before you can resume add-on subscriptions.
        </div>
      )}
    </div>
  </div>
);

export default ResumeAddonSubscription;
